import { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, TypedDispatch } from 'redux/store'
import { FREQUENCY } from '@classy/campaign-page-blocks'
import { checkWalletType } from 'redux/features/stripePaymentRequest'
import {
  PAYMENT_METHOD_CONFIGS,
  PaymentMethodConfig,
  paymentMethodMap,
} from 'modules/flow/components/payment-methods-step/utils/payment-method-configs'
import { PaymentMethod as PaymentType, PaymentProcessor } from 'models/ChargeIntent'
import useConfig from 'modules/config/state/use-config'
import useOrder from 'modules/order/state/order/use-order'
import { useGetOrgPlanFeaturesQuery } from 'redux/services/organization'
import { useGetPaymentMethodsQuery } from 'redux/services/paymentMethods'
import { UAParser } from 'ua-parser-js'

type PaymentMethod = {
  id: string
  name: string
  status: string
  type: string
  currency: string
}

// Main hook to fetch active payment method configurations
export const useActivePaymentMethodConfigs = (): PaymentMethodConfig[] => {
  const dispatch = useDispatch()

  // Get relevant states
  const isDWEnabled = _useIsDWEnabled()
  const isACHEnabled = _useIsACHEnabled()
  const isVenmoEnabled = _useIsVenmoEnabled()
  const activePaymentMethods = _useActivePaymentMethods()

  // Memoize the calculation of active payment methods
  return useMemo(() => {
    const seenTypes: { [key: string]: boolean } = {}

    return activePaymentMethods.reduce<PaymentMethodConfig[]>((result, paymentMethod) => {
      let paymentType = paymentMethod.type

      if (seenTypes[paymentType]) {
        return result
      }

      /**
       * For MVP, we only support Paypal Commerce
       * See. https://classydev.atlassian.net/browse/CL-41943?focusedCommentId=275397
       */
      if (paymentType === 'PayPal' && paymentMethod.name !== 'PAYPAL_COMMERCE') {
        return result
      }

      // use Plaid for ACH unless org settings enable non-Plaid ACH
      if (paymentType === 'ACH' && !isACHEnabled) {
        paymentType = PaymentType.Plaid
      }

      // Conditionally filter out DW
      if (paymentType === 'DW' && !isDWEnabled) {
        return result
      }

      // Conditionally filter out Venmo
      if (paymentType === 'Venmo' && !isVenmoEnabled) {
        return result
      }

      seenTypes[paymentType] = true

      const config = getPaymentMethodConfig(paymentType, dispatch)

      result.push({
        ...config,
        key: paymentType,
        processor: paymentMethod.name as PaymentProcessor,
      })

      return result
    }, [])
  }, [activePaymentMethods, isDWEnabled, isACHEnabled, isVenmoEnabled, dispatch])
}

// Utility function to get the config for a given payment method
export const getPaymentMethodConfig = (
  paymentMethod: string,
  dispatch: TypedDispatch<RootState>,
): PaymentMethodConfig => {
  const walletType = paymentMethod === 'DW' ? dispatch(checkWalletType()) : null

  return walletType
    ? PAYMENT_METHOD_CONFIGS[walletType]
    : PAYMENT_METHOD_CONFIGS[paymentMethodMap[paymentMethod]]
}

// Utility hook to get active payment methods
const _useActivePaymentMethods = () => {
  const order = useOrder()
  const config = useConfig()

  const { data } = useGetPaymentMethodsQuery({
    campaignId: config.campaignId,
    currency: order.currency.value,
  })

  // Exclude Paypal, Venmo and ACH from none USD currencies
  if (data?.paymentMethods && order.currency.value !== 'USD') {
    const excludedTypes = ['ACH', 'PayPal', 'Venmo', 'PayPal_Commerce']

    return data.paymentMethods.filter((method) => !excludedTypes.includes(method.type))
  }

  return data ? data.paymentMethods : []
}

// Utility hook to check if DW (Digital Wallet) is enabled
const _useIsDWEnabled = () => {
  const canMakePayment = useSelector(
    (state: RootState) => state.order.stripePaymentRequest.canMakePayment,
  )

  return useMemo(() => {
    return canMakePayment
  }, [canMakePayment])
}

// Utility hook to check if ACH is enabled
const _useIsACHEnabled = () => {
  const config = useConfig()
  const { data: planFeatures } = useGetOrgPlanFeaturesQuery(config.organizationId)

  return useMemo(
    () => (planFeatures ? planFeatures.ach_account_and_routing : false),
    [planFeatures],
  )
}

// Utility hook to check if Venmo is enabled
const _useIsVenmoEnabled = (): boolean => {
  const order = useOrder()

  // Parse the user's OS and browser information
  const parser = new UAParser()
  const os = parser.getOS()?.name ?? ''
  const browser = parser.getBrowser()?.name ?? ''

  // Memoize Venmo check logic
  return useMemo(() => {
    // Venmo is not supported for recurring payments
    if (order.frequency.value !== FREQUENCY.ONE_TIME) {
      return false
    }

    // Venmo is only supported on iOS with Mobile Safari and Android with Chrome
    return !(
      (os === 'iOS' && browser !== 'Mobile Safari') ||
      (os === 'Android' && browser !== 'Chrome')
    )
  }, [order.frequency.value, os, browser])
}
